import type { ReactNode } from 'react';
import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Flex } from '@atlaskit/primitives/compiled';

import { SidePanel, SidePanelBody, SidePanelFooter, SidePanelHeader } from '@confluence/side-panel';

const i18n = defineMessages({
	title: {
		id: 'space-suggestions.review-panel.title',
		defaultMessage: 'Review suggestion',
		description: 'Title for the suggestion review panel',
	},
	close: {
		id: 'space-suggestions.review-panel.close-label',
		defaultMessage: 'Close',
		description: 'Accessibility label for the close button on the suggestion review panel',
	},
	cancel: {
		id: 'space-suggestions.review-panel.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button on the suggestion review panel',
	},
	approve: {
		id: 'space-suggestions.review-panel.approve-button',
		defaultMessage: 'Approve',
		description: 'Label for the approve button on the suggestion review panel',
	},
});

const DEFAULT_PANEL_WIDTH = 320;

export type SuggestionReviewPanelProps = {
	onClose: () => void;
	onCancel: () => void;
	onApprove: () => void;
	children: ReactNode;
	isApproveDisabled?: boolean;
	/** Panel width in pixels. Defaults to 320. */
	width?: number;
	/** Total height of banners/headers above the panel, used to offset vertical position. */
	headerHeight?: number;
};

export const SuggestionReviewPanel = memo(
	({
		onClose,
		onCancel,
		onApprove,
		children,
		isApproveDisabled = false,
		width = DEFAULT_PANEL_WIDTH,
		headerHeight = 0,
	}: SuggestionReviewPanelProps) => {
		const { formatMessage } = useIntl();

		return (
			<SidePanel isFixed width={width} headerHeight={headerHeight} panelTop={headerHeight}>
				<SidePanelHeader>
					<Flex direction="row" justifyContent="space-between" alignItems="center">
						<Heading size="small">{formatMessage(i18n.title)}</Heading>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.close)}
							appearance="subtle"
							onClick={onClose}
						/>
					</Flex>
				</SidePanelHeader>

				<SidePanelBody>{children}</SidePanelBody>

				<SidePanelFooter>
					<Flex direction="row" gap="space.100" justifyContent="end">
						<Button onClick={onCancel}>{formatMessage(i18n.cancel)}</Button>
						<Button appearance="primary" onClick={onApprove} isDisabled={isApproveDisabled}>
							{formatMessage(i18n.approve)}
						</Button>
					</Flex>
				</SidePanelFooter>
			</SidePanel>
		);
	},
);
